import { createContext, FunctionComponent, ReactNode, useContext, useEffect, useReducer } from 'react'

import { useContent } from '@/hooks/content'
import { Subcategory } from '@/services/m-commerce-api/product/subcategories'
import { Category } from '@/services/m-commerce-api/product/categories'
import { HomePageContent } from '@/services/cms-api/home-page-content/types'
import { ServiceCategory } from '@/services/cms-api/service-menu/types'
import { useCmsApi } from '@/services/cms-api'
import useIntl from '@/utils/intl'
import { useLanguage } from './LanguageContext'
import { useToast } from './ToastContext'

interface Prop {
  children?: ReactNode
}
export interface ContentState {
  categoryTree?: Category[]
  subcategories?: Subcategory[]
  homePageContent?: HomePageContent
  serviceCategories?: ServiceCategory[]
}
export interface ContentContext {
  state: ContentState
}

const contentContext = createContext<ContentContext>({
  state: {
    categoryTree: [],
    subcategories: [],
    homePageContent: undefined,
    serviceCategories: []
  }
})

export const useContentContext = (): ContentContext => {
  return useContext(contentContext)
}

export const ContentProvider: FunctionComponent<Prop> = ({ children }) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { locale } = useLanguage()

  const [state, setState] = useReducer(
    (state: ContentState, newState: ContentState) => ({ ...state, ...newState }),
    {
      categoryTree: [],
      subcategories: [],
      homePageContent: undefined,
      serviceCategories: []
    }
  )
  const { getCategoryTree, getSubcategories, getHomePageContent } = useContent()
  const { getServiceCategories } = useCmsApi()
  useEffect(() => {
    getCategoryTree()
      .then(tree => {
        setState({ categoryTree: tree })
      })
      .catch(e => {
        showError(translate('request.failed'))
      })
    getSubcategories()
      .then(data => {
        setState({ subcategories: data })
      })
      .catch(e => {
        showError(translate('request.failed'))
      })
    getHomePageContent()
      .then(data => {
        setState({ homePageContent: data })
      })
      .catch(e => {
        showError(translate('request.failed'))
      })
    getServiceCategories()
      .then(data => {
        setState({ serviceCategories: data })
      })
      .catch(e => {
        showError(translate('request.failed'))
      })
  }, [locale])

  return (
    <contentContext.Provider
      value={{ state }}
    >
      {children}
    </contentContext.Provider>
  )
}
